import React from 'react';

export interface VBButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  onClick?: (e?: any) => void;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  'aria-label'?: string;
  'aria-busy'?: boolean;
  'aria-haspopup'?: boolean | 'menu';
  'aria-expanded'?: boolean;
}

export const VBButton: React.FC<VBButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  iconPosition = 'left',
  onClick,
  disabled = false,
  className = '',
  children,
  'aria-label': ariaLabel,
  'aria-busy': ariaBusy,
  'aria-haspopup': ariaHasPopup,
  'aria-expanded': ariaExpanded
}) => {
  const baseClasses =
    // focus-visible (not focus) so the ring only shows for keyboard users;
    // on mouse click it was rendering a ring that got clipped by overflow/
    // rounded ancestors (e.g. the FilterBar scroller, reaction chips).
    'inline-flex items-center justify-center font-medium rounded-lg transition-colors cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-vb-primary';

  const variantClasses = {
    primary: 'bg-vb-primary hover:bg-vb-primary-hover text-white',
    secondary:
      'bg-vb-surface border border-vb-border hover:bg-vb-surface-hover text-vb-text-primary dark:bg-vb-surface-dark dark:border-vb-border-dark dark:hover:bg-vb-surface-hover-dark dark:text-vb-text-primary-dark',
    outline:
      'bg-transparent border border-vb-primary text-vb-primary hover:bg-vb-primary hover:text-white',
    ghost:
      'bg-transparent hover:bg-vb-surface-hover text-vb-text-primary dark:hover:bg-vb-surface-hover-dark dark:text-vb-text-primary-dark',
    danger: 'bg-vb-danger hover:bg-red-600 text-white'
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-1 gap-1.5',
    md: 'text-sm px-3 py-1.5 gap-2',
    lg: 'text-sm px-4 py-2 gap-2'
  };

  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : '';
  const widthClass = fullWidth ? 'w-full' : '';

  const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (!disabled && onClick) {
      onClick(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
      aria-busy={ariaBusy}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${disabledClasses} ${className}`}
    >
      {icon && iconPosition === 'left' && <span className="flex-shrink-0">{icon}</span>}
      {children}
      {icon && iconPosition === 'right' && <span className="flex-shrink-0">{icon}</span>}
    </div>
  );
};
